import type { ReactNode } from 'react';
import { Navigate, useParams } from 'react-router';

import { stageToStep, useSearchSession } from '@entities/search-session';

import { orderStageView, stageRoute } from '../model/orderStage';

export type OrderScreen = 'assistant' | 'analysis' | 'results' | 'top4' | 'handover';

/** Минимальный шаг воронки, на котором экран вообще имеет содержимое. */
const SCREEN_MIN_STEP: Record<OrderScreen, number> = {
  assistant: 1,
  analysis: 2,
  results: 2,
  // Четвёрка существует только после оплаты (шаг 4): до неё её физически нет,
  // и открывать экран не с чем (MVP_PRD §34.3).
  top4: 4,
  // Сопровождение существует только после согласия с предложением (шаг 7):
  // до него нет ни контактов, ни зафиксированных условий, ни чек-листа
  // (MVP_PRD §39). Backend отвечает `404 HANDOVER_NOT_FOUND`, guard лишь не
  // ведёт туда раньше времени.
  handover: 7,
};

interface OrderStageGuardProps {
  screen: OrderScreen;
  children: ReactNode;
}

/**
 * Навигационный guard стадии заказа (FE-200).
 *
 * **Это удобство, а не защита.** Права проверяет backend: чужой заказ отдаёт
 * `404`, закрытый шаг — `402` (MVP_TECH_RFC §21). Здесь решается одно: не
 * показывать экран, на котором у заказа заведомо нечего показывать, — например,
 * результаты подбора, который ещё не запускался.
 *
 * **Назад ходить можно.** Пройденный шаг открыт всегда (MVP_PRD §33.1): guard
 * уводит только с экранов, до которых заказ ещё не дошёл. Иначе пользователь не
 * смог бы вернуться к диалогу из готовых результатов.
 *
 * Пока состояние заказа не загружено, экран рисуется как есть: у страницы свои
 * скелетоны, а мигание «загрузка → редирект» хуже секунды ожидания.
 */
export function OrderStageGuard({ screen, children }: OrderStageGuardProps) {
  const { searchId = null } = useParams();
  const { data } = useSearchSession(searchId);

  if (!data) return <>{children}</>;

  // Оплаченный заказ не выгоняют с шагов, которые он уже прошёл. Уточнение
  // критериев внутри такого заказа возвращает стадию к началу воронки
  // (MVP_PRD §35.4), и без этой строки пользователь, написавший Karl из рельса
  // на четвёрке, немедленно улетал бы с неё на экран диалога — то есть терял
  // из виду ровно то, что уточняет. Признак оплаты — `paid`, а не стадия.
  if (data.session.paid && (screen === 'results' || screen === 'top4')) return <>{children}</>;

  /*
    Прогон у заказа уже есть — значит, на экране результатов ему есть что
    показать, и уводить оттуда нельзя.

    Правка критериев из рельса возвращает **неоплаченный** заказ к началу
    воронки (`refresh_after_criteria_change`), и по стадии он снова «шаг 1».
    Без этой строки сообщение Karl'у прямо с результатов выбрасывало человека
    на экран диалога — то есть уводило ровно от того, что он уточняет.
    Перестройка идёт по подтверждению в рельсе, а не сама (CLAUDE.md §8).
  */
  if (screen === 'results' && data.session.current_search_run_id !== null) {
    return <>{children}</>;
  }

  // Стадия ведёт ровно на этот экран — уводить некуда. Проверка идёт первой и
  // покрывает разрыв между шагом и адресом: `paid` стоит на шаге 3, но заказу
  // на этой стадии показывают четвёрку (отбор уже поставлен в очередь).
  // Без неё guard уводил бы с экрана на него же.
  if (orderStageView(data.session.stage).screen === screen) return <>{children}</>;

  const step = stageToStep(data.session.stage);
  // Заказ вне воронки (закрыт, в архиве) шага не имеет — и уводить его
  // некуда: архивный заказ остаётся доступен на чтение (MVP_PRD §20).
  if (step === null || step >= SCREEN_MIN_STEP[screen]) return <>{children}</>;

  return <Navigate to={stageRoute(data.session.stage, data.session.id)} replace />;
}
